import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useHorizontalScrollArrows — shared behaviour for the flanking/overlay
 * prev-next arrows on horizontally-scrollable rows (blog CategoryPillNav pill
 * strip, gym-profile mobile photo carousel).
 *
 * Attach `ref` to the scroll container. `isOverflowing` is false until the
 * effect measures after mount, so SSR + first client render agree (no arrows)
 * and the arrows only appear once the content actually overflows. `canScrollPrev`
 * / `canScrollNext` drive the disabled state; `scrollByFraction` nudges by a
 * fraction of the visible width (honouring prefers-reduced-motion).
 */
export function useHorizontalScrollArrows<T extends HTMLElement = HTMLDivElement>() {
    const ref = useRef<T>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const recalc = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setIsOverflowing(maxScroll > 1);
        setCanScrollPrev(el.scrollLeft > 1);
        setCanScrollNext(el.scrollLeft < maxScroll - 1);
    }, []);

    useEffect(() => {
        recalc();
        const el = ref.current;
        if (!el) return;
        el.addEventListener('scroll', recalc, { passive: true });
        window.addEventListener('resize', recalc);
        return () => {
            el.removeEventListener('scroll', recalc);
            window.removeEventListener('resize', recalc);
        };
    }, [recalc]);

    const scrollByFraction = useCallback((direction: -1 | 1, fraction = 0.8) => {
        const el = ref.current;
        if (!el) return;
        const reduce = typeof window !== 'undefined'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollBy({ left: direction * el.clientWidth * fraction, behavior: reduce ? 'auto' : 'smooth' });
    }, []);

    return { ref, isOverflowing, canScrollPrev, canScrollNext, scrollByFraction, recalc };
}
